/**
 * <TutorQuizReview> — le quiz de prérequis d'une activité, relu par le tuteur (organism).
 *
 * La vue « Quizz » de l'ancienne fiche (`PrerequisiteQuizDisplay`, côté tuteur), la même dans la fiche
 * (`<TutorActivityDetailsModal>`) et au centre du live (`<TutorLiveActivityView>`) : le score
 * (`<QuizScore>`), l'avis du tuteur sur le quiz quand on le lui demande (« Ce quiz est-il utile ? »,
 * `<CorrectionFeedback>`), puis « Ses réponses » — chaque question (`<QuizReviewQuestion
 * reader="tutor">`, « Sa réponse ») et, quand l'écran le permet, le drapeau au bout de sa ligne, comme
 * l'ancien `QuizQuestionSignalButton` (un drapeau, pas une ligne « Signaler » de plus sous chaque
 * question). Pas de quiz : « Aucun quiz prérequis disponible ».
 *
 * `compact` (le centre du live) : la densité de l'ancienne fiche, que le live du 04/11 a regrettée —
 * le score tient en une chip (« 3/5 · 60 % »), l'avis à côté, et les réponses suivent sans intitulé,
 * une ligne serrée par question (`<QuizReviewQuestion compact>`). La fiche garde l'anneau (défaut).
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { type UICorrectionFeedback } from "../../../ports/primary/ui/types/studentActivity"
import { type UIQuizReview } from "../../../ports/primary/ui/types/studentQuizReview"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { CardList, CardListRow } from "../../molecules/CardList"
import { CorrectionFeedback } from "../../molecules/CorrectionFeedback"
import { EmptyState } from "../../molecules/EmptyState"
import { QuizReviewQuestion } from "../../molecules/QuizReviewQuestion"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"
import { QuizScore } from "../../molecules/QuizScore"

export type TutorQuizReviewProps = {
    review: UIQuizReview | null
    /** L'avis du tuteur sur le quiz. Absent : pas de ligne. */
    feedback?: { value: UICorrectionFeedback | null; onChange: (value: UICorrectionFeedback) => void } | null
    /** « Signaler » une question (son index), par le drapeau au bout de sa ligne. Absent : pas de drapeau. */
    onSignalQuestion?: (index: number) => void
    /** Le score en une ligne, les réponses aussitôt (le centre du live). Défaut `false`. */
    compact?: boolean
    testID?: string
}

const TEXTS = {
    empty: "Aucun quiz prérequis disponible",
    answers: "Ses réponses",
    signal: (number: number) => `Signaler la question ${number}`,
    feedback: { question: "Ce quiz est-il utile ?", yes: "Utile", no: "Pas utile" },
    score: (score: number, total: number, percentage: number) => `${score}/${total} · ${percentage} %`,
    passed: "Quiz réussi",
    failed: "Quiz non réussi",
    notAttempted: "Pas encore tenté",
}

export function TutorQuizReview(props: TutorQuizReviewProps) {
    const { review, feedback, onSignalQuestion, compact = false, testID } = props
    if (review == null) return <EmptyState iconName="tasks" title={TEXTS.empty} testID={testID} />
    const feedbackRow = feedback != null && (
        <CorrectionFeedback value={feedback.value} onChange={feedback.onChange} texts={TEXTS.feedback} />
    )
    // Pas encore tenté : ni score ni avis, les questions et leur bonne réponse (C-88 du registre du live).
    const attempted = review.attempted !== false
    return (
        <Stack gap={compact ? "2" : "4"} testID={testID}>
            {!attempted ? (
                <Row gap="3" align="center" style={styles.compactHead}>
                    <Badge text={TEXTS.notAttempted} variant="neutral" truncate={false} />
                </Row>
            ) : compact ? (
                <Row gap="3" align="center" style={styles.compactHead}>
                    <Badge
                        text={TEXTS.score(review.score, review.total, review.percentage)}
                        variant={review.passed ? "success" : "danger"}
                        tinted
                        truncate={false}
                    />
                    <Text size="text-sm" weight="bold" color="text-muted">
                        {review.passed ? TEXTS.passed : TEXTS.failed}
                    </Text>
                    {feedbackRow}
                </Row>
            ) : (
                <>
                    <QuizScore {...review} />
                    {feedbackRow}
                    <SectionLabel>{TEXTS.answers}</SectionLabel>
                </>
            )}
            <CardList>
                {review.questions.map((question, index) => (
                    // La question pose déjà son retrait vertical : la ligne n'en rajoute pas au live.
                    <CardListRow key={index} divided={index > 0} px={compact ? "3" : "4"} py={compact ? "0" : "3"}>
                        <QuizReviewQuestion
                            index={index}
                            question={question}
                            reader="tutor"
                            compact={compact}
                            attempted={attempted}
                            trailing={
                                onSignalQuestion == null ? undefined : (
                                    <IconButton
                                        iconName="flag"
                                        appearance="transparent"
                                        shape="square"
                                        size={32}
                                        iconSize={16}
                                        iconColor="text-muted"
                                        hitSlop={6}
                                        onPress={() => onSignalQuestion(index)}
                                        accessibilityLabel={TEXTS.signal(index + 1)}
                                    />
                                )
                            }
                        />
                    </CardListRow>
                ))}
            </CardList>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    compactHead: { flexWrap: "wrap", rowGap: theme.spacing["2"] },
}))
